import { useEffect, useRef } from "react";
import { CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { WA_MOCK, WhatsAppMockInputBar } from "@/components/landing/shared/WhatsAppMockChrome";
import { WhatsAppMockMessageList } from "@/components/landing/shared/WhatsAppMockMessageList";
import { WhatsAppAttachmentBubble } from "@/components/landing/shared/WhatsAppAttachmentBubble";
import type { UseCaseMessage, UseCaseScenario } from "./useCaseScenarios";

type UseCasePhoneScreenProps = {
  scenario: UseCaseScenario;
};

const HEADER = {
  whatsapp: "#008069",
  instagram: "linear-gradient(135deg, #833AB4 0%, #C13584 45%, #E1306C 100%)",
} as const;

function getChannel(headerLabel: string): "whatsapp" | "instagram" {
  return headerLabel.toLowerCase().startsWith("instagram") ? "instagram" : "whatsapp";
}

export function UseCasePhoneScreen({ scenario }: UseCasePhoneScreenProps) {
  const Icon = scenario.icon;
  const chatRef = useRef<HTMLDivElement>(null);
  const messages = scenario.messages;
  const channel = getChannel(scenario.headerLabel);
  const headerStyle =
    channel === "instagram"
      ? { background: HEADER.instagram }
      : { backgroundColor: HEADER.whatsapp };

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [scenario.id]);

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ backgroundColor: WA_MOCK.chatBg }}>
      <header className="relative z-10 shrink-0" style={headerStyle}>
        <div className="h-[30px] shrink-0" aria-hidden />
        <div className="flex min-h-[36px] items-center justify-between gap-2 px-2.5 pb-2">
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-[8px] font-medium text-white/75">{scenario.headerLabel}</p>
            <p className="truncate text-[11px] font-semibold leading-snug text-white">{scenario.title}</p>
          </div>
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
          >
            <Icon className="h-4 w-4 text-white" strokeWidth={1.75} aria-hidden />
          </div>
        </div>
      </header>

      <WhatsAppMockMessageList
        key={scenario.id}
        ref={chatRef}
        scrollClassName="flex-1"
        contentClassName="!px-3 gap-1.5"
      >
        {messages.map((msg, i) => (
          <ChatBubble key={`${scenario.id}-${i}`} message={msg} delayMs={Math.min(i * 60, 420)} />
        ))}
      </WhatsAppMockMessageList>

      <WhatsAppMockInputBar />
    </div>
  );
}

function ChatBubble({
  message,
  delayMs,
}: {
  message: UseCaseMessage;
  delayMs: number;
}) {
  const kind = message.kind ?? "text";

  if (kind === "pdf" || kind === "link") {
    return (
      <div className="use-case-bubble-enter" style={{ animationDelay: `${delayMs}ms` }}>
        <WhatsAppAttachmentBubble
          side={message.side}
          kind={kind}
          caption={message.text}
          fileName={message.fileName ?? (kind === "pdf" ? "Documento.pdf" : "Link")}
          fileMeta={message.fileMeta}
          linkUrl={message.linkUrl}
        />
      </div>
    );
  }

  const isAgent = message.side === "agent";

  return (
    <div
      className={cn("use-case-bubble-enter flex w-full", isAgent ? "justify-end" : "justify-start")}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div
        className={cn(
          "max-w-[90%] rounded-lg px-2.5 py-2 text-[11px] leading-[1.45] shadow-sm",
          isAgent ? "rounded-tr-none" : "rounded-tl-none",
        )}
        style={{
          backgroundColor: isAgent ? WA_MOCK.outgoing : WA_MOCK.incoming,
          color: WA_MOCK.text,
        }}
      >
        <p>{message.text}</p>
        {isAgent && (
          <div className="mt-0.5 flex justify-end">
            <CheckCheck className="h-3 w-3" style={{ color: WA_MOCK.tick }} aria-hidden />
          </div>
        )}
      </div>
    </div>
  );
}
